import { FormTextField } from '@orbis-systems/orbis-react-toolkit';
import { ErrorMessage, FormikProps } from 'formik';
import React from 'react';

import { getCurrentUser } from '@app/redux/auth/auth.selectors';
import { useAppSelector } from '@app/redux/hooks';

import { ButtonBase, Grid, Typography } from '@mui/material';

import Form from '@components/forms';
import SubmitButton from '@components/submitButton';

import useVerifyPhoneForm from './hooks';
import { VerifyPhoneFormProps, VerifyPhoneFormValues } from './interfaces';
import { BackSubmitButton } from './styles';

function VerifyPhoneForm({ initialValues, onSubmit, isPostLogin, onDecline }: VerifyPhoneFormProps) {
	const { isSendingVerificationPhoneCode, handleSendVerificationPhoneCode, formik } = useVerifyPhoneForm({
		initialValues,
		onSubmit,
	});
	const currentUser = useAppSelector(getCurrentUser);

	return (
		<VerifyPhoneContainer
			handleSendVerificationPhoneCode={handleSendVerificationPhoneCode}
			isSendingVerificationPhoneCode={isSendingVerificationPhoneCode}
			phone={currentUser?.phone}
			isPostLogin={isPostLogin}
			onDecline={onDecline}
			formik={formik}
		/>
	);
}

interface VerifyPhoneContainerProps {
	handleSendVerificationPhoneCode: () => void;
	isSendingVerificationPhoneCode: boolean;
	isPostLogin?: boolean;
	onDecline?: () => void;
	formik: FormikProps<VerifyPhoneFormValues>;
	phone?: string;
}

export const VerifyPhoneContainer = ({
	handleSendVerificationPhoneCode,
	isSendingVerificationPhoneCode,
	isPostLogin,
	onDecline,
	formik,
	phone,
}: VerifyPhoneContainerProps) => {
	return (
		<Form formik={formik}>
			<Grid container mb={1}>
				<Grid item xs={12} textAlign={'right'} sx={{ order: isPostLogin ? 5 : 1 }}>
					<ButtonBase onClick={handleSendVerificationPhoneCode} disabled={isSendingVerificationPhoneCode}>
						<Typography variant="body2">
							<b>Resend code?</b>
						</Typography>
					</ButtonBase>
				</Grid>

				<Grid
					item
					xs={12}
					mt={2}
					sx={{
						color: theme => theme.palette.error.main,
						textAlign: 'center',
						fontSize: 14,
						order: 2,
					}}
				>
					<ErrorMessage name="resendVerificationPhoneCodeError" />
				</Grid>

				{isPostLogin && (
					<Grid item xs={12}>
						<Typography variant="body1" mb={2} sx={{ fontSize: 16, fontWeight: 400 }}>
							A verification code has been sent to your phone number. Enter it to verify your account.
						</Typography>

						{phone && <FormTextField label={'Phone number'} autoComplete="off" name={'phone'} value={phone} disabled />}
					</Grid>
				)}

				<Grid item xs={12} mt={4} sx={{ order: 4 }}>
					<FormTextField
						focused
						name={'code'}
						label={'Verification code'}
						autoComplete="off"
						disabled={formik.isSubmitting || isSendingVerificationPhoneCode}
					/>
				</Grid>
				<Grid item xs={12} mt={4} sx={{ order: 6 }}>
					<Grid container spacing={3}>
						{onDecline && (
							<Grid item xs={6}>
								<BackSubmitButton
									fullWidth
									type="button"
									variant="contained"
									color="secondary"
									onClick={onDecline}
									disabled={formik.isSubmitting || isSendingVerificationPhoneCode}
								>
									Back
								</BackSubmitButton>
							</Grid>
						)}

						<Grid item xs={onDecline ? 6 : 12}>
							<SubmitButton label={'Verify'} disabled={formik.isSubmitting || isSendingVerificationPhoneCode} />
						</Grid>
					</Grid>
				</Grid>
			</Grid>
		</Form>
	);
};

export default VerifyPhoneForm;
